import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import classNames from 'classnames';
import { ColumnType, TableFilter } from '../..';
import { useOutsideClick } from 'src/Hooks';
import getFilterControl from './DefaultFilters';
import { ReactComponent as ClearIcon } from "../assets/clearBtn.svg";
import st from "./TableFilter.module.scss";

interface IFilterProps {
	/** Режим конструктора */
	/// TODO: Вынести в редакс
	isConstructor?: boolean;
	/** ИД колонки */
	columnId: string;
	/** Список всех типов колонок */
	columnTypes: Array<ColumnType>;
	/** Объект фильтра */
	filter?: TableFilter;
};

type FilterValue = string | string[];

/** Компонента-контейнер для фильтра таблицы */
const Filter: React.FC<IFilterProps> = ( props ) => {

	const { columnId, columnTypes, filter } = props;

	if ( !columnId )
		throw new Error( "Не Id колонки!" );
	if ( !columnTypes || !columnTypes.length )
		throw new Error( "Типы колонок не определены!" );

	const wrapperRef = useOutsideClick<HTMLDivElement>( () => setIsShowModal(false) );
	const [ isShowModal, setIsShowModal ] = useState<boolean>(false);
	// Кэшируем поле значение
	const [ value, setValue ] =	useState<FilterValue>('');

	// const columnType = columnTypes.find( c => c.id === columnId );
	const columnType = useMemo( () => {
		let col = columnTypes.find( c => c.id === columnId );
		if ( !col ) {
			console.warn( `Колонка с ИД ${columnId} не найдена в списке колонок реестра(` );
			col = {
				id: columnId,
				type: "string"
			};
		}
		return col;
	}, [ columnId, columnTypes ] );
	const sType = columnType?.type?.toString() ?? "string";

	// Обновляем кэш значения при изменениях в состоянии фильтров 
	// или при открытии/закрытии ( для сброса введенного значения без применении фильтра )
	useEffect( () => {
		const val = getFilterValue();
		setValue( val );
	}, [ filter?.state, isShowModal ] );

	const onKeyUp = ( e: KeyboardEvent ) => {
		if ( !isShowModal ) return;
		if ( e.code === "Enter" ) {
			applyValueHandler(e);
		}
		else if ( e.code === "Escape" ) {
			cancelHandler(e);
		}
	};

	useLayoutEffect( () => {
		window.addEventListener( "keyup", onKeyUp );
		return () => {
			window.removeEventListener( "keyup", onKeyUp );
		};
	// eslint-disable-next-line
	}, [ isShowModal, onKeyUp ] );

	/** Применение фильтра */
	const applyValueHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent ) => {
		e.preventDefault();
		e.stopPropagation();
		setFilterValue( value );
		setIsShowModal(false);
	};

	/** Отмена ( закрытие модалки ) */
	const cancelHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent ) => {
		e.preventDefault();
		e.stopPropagation();
		setIsShowModal(false);
	};

	/** Открытие модалки фильтра */
	const onOpenModalHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		setIsShowModal(true);
		const wr = wrapperRef.current;
		if ( wr ) {
			wr.scrollIntoView( {behavior: "smooth", block: "end", inline: "start"});
		}
	};

	/** Вызывается при нажатии на крестик */
	const onClearOrCloseModalHandler = (e: React.MouseEvent ) => {
		e.preventDefault();
		e.stopPropagation();
		if ( isShowModal )
			setIsShowModal( false );
		else
			setFilterValue('');
	};

	/** Возвращает текущее значение фильтра из состояния */
	const getFilterValue = (): FilterValue => {
		const emptyValue = columnType?.isBetween || columnType?.multiple ? [] : "";
		if ( !filter || !filter.state || !filter.state.length ) return emptyValue;
		const index = filter.state.findIndex( f => f.id === columnId );
		if ( index !== -1 ) {
			return filter?.state[index].value as FilterValue;
		}
		return emptyValue;
	};

	/** Устанавливает значение фильтра в стэйт */
	const setFilterValue = ( val: FilterValue ) => {
		filter?.setState( prev => {
			const newFilters = [ ...prev ];
			const index = newFilters.findIndex( f => f.id === columnId );
			if ( index !== -1 )
				newFilters[index].value = val;
			else
				newFilters.push( { id: columnId, value: val });
			return newFilters;
		});
	};

	// получаем компоненту фильтра
	const Filter = getFilterControl( sType );
	// Определим пропсы для фильтра
	const filterProps = { 
		columnId, columnType,
		isShowModal, setIsShowModal, 
		value, setValue,
		cancelHandler, applyValueHandler 
	};

	// Отрисуем обертку с фильтром внутри
	const classes = classNames( st.filterWrapper, { [st.active]: isShowModal } );
	return (
		<div id={"filter-" + columnId} ref={wrapperRef} className={classes} onClick={onOpenModalHandler}>
			<Filter {...filterProps} />
			{ ( !isNullOrEmpty(value) || isShowModal ) && <ClearIcon className={st.clearIcon} onClick={onClearOrCloseModalHandler} width={28} height={28} /> }
		</div>
	);
};

const isNullOrEmpty = ( val: any ): boolean => {
	if ( val === undefined || val === null ) return true;
	else if ( Array.isArray( val ) ) return !val.length;
	else return !val;
};

export default Filter;
